import { useMemo, useState } from 'react';

import { api } from '../../services/api';
import {
  actionsForStatus,
  callerDisplayName,
  callerInitials,
  callDirectionLabel,
  callStatusClass,
  channelLabel,
  endReasonLabels,
  formatCallDate,
  formatDuration,
  isActiveCallStatus,
  priorityClassName,
  priorityLabel,
  routeLabels,
  statusLabels,
} from '../../services/call-helpers';
import type { CallRecord, Chat } from '../../types';
import VideoCall from './VideoCall';

type Props = {
  activeCalls: CallRecord[];
  chats: Chat[];
  onReload: () => void;
};

const CallDashboard = ({ activeCalls, chats, onReload }: Props) => {
  const [callLog, setCallLog] = useState<CallRecord[]>([]);
  const [showLog, setShowLog] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);
  const [loadingLogError, setLoadingLogError] = useState<string | null>(null);
  const [activeVideoCall, setActiveVideoCall] = useState<CallRecord | null>(null);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

  const active = useMemo(
    () => activeCalls.filter((call) => isActiveCallStatus(call.status)),
    [activeCalls],
  );

  const loadLog = async () => {
    setLoadingLog(true);
    setLoadingLogError(null);
    try {
      const log = await api.callLog(50);
      setCallLog(log);
      setShowLog(true);
    } catch {
      setLoadingLogError('Geschiedenis kon niet worden geladen.');
    } finally {
      setLoadingLog(false);
    }
  };

  const handleApi = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      onReload();
    } catch {
      // ignore in UI; the server returns 4xx/5xx and the user can retry
    }
  };

  const handleAccept = (callId: string) =>
    handleApi(async () => {
      const accepted = await api.acceptCall(callId);
      setActiveVideoCall(accepted);
    });

  const handleReject = (callId: string) =>
    handleApi(() => api.rejectCall(callId));

  const handleEnd = (callId: string) =>
    handleApi(async () => {
      await api.endCall(callId);
      setActiveVideoCall(null);
    });

  const handleStartAvatar = (callId: string) =>
    handleApi(() => api.startAvatar(callId));

  const handleStopAvatar = (callId: string) =>
    handleApi(() => api.stopAvatar(callId));

  const handleTakeover = (callId: string) =>
    handleApi(() => api.avatarTakeover(callId));

  const handleJoinCall = (call: CallRecord) => {
    if (call.roomUrl) {
      setActiveVideoCall(call);
    }
  };

  const handleHold = (callId: string) =>
    handleApi(() => api.holdCall(callId));

  const handleResume = (callId: string) =>
    handleApi(() => api.resumeCall(callId));

  const handleTransfer = (callId: string) => {
    const operatorId = prompt('Naar welke agent wil je doorschakelen? (geef user ID)');
    if (!operatorId) return;
    handleApi(() => api.transferCall(callId, operatorId));
  };

  const handleCallBack = (call: CallRecord) =>
    handleApi(() =>
      api.outboundCall({
        accountId: call.accountId,
        phone: call.from,
        isVideo: call.isVideo,
      }),
    );

  const toggleDetails = (callId: string) =>
    setExpandedCallId((current) => (current === callId ? null : callId));

  if (activeVideoCall) {
    return (
      <VideoCall
        call={activeVideoCall}
        onEnd={handleEnd}
        onAvatarStart={handleStartAvatar}
        onAvatarStop={handleStopAvatar}
        onTakeover={handleTakeover}
      />
    );
  }

  return (
    <section className="call-dashboard" aria-label="Oproepen">
      <header className="panel-header">
        <div>
          <p className="eyebrow">Telefonie</p>
          <h2>Oproepen</h2>
        </div>
        <button
          type="button"
          className="button secondary small"
          onClick={showLog ? () => setShowLog(false) : loadLog}
          disabled={loadingLog}
        >
          {loadingLog ? 'Laden…' : showLog ? 'Verberg geschiedenis' : 'Geschiedenis'}
        </button>
      </header>

      <div className="call-section active-calls">
        <h3 className="call-section-title">Actief</h3>
        {active.length === 0 && (
          <p className="empty-state">Geen actieve oproepen.</p>
        )}
        {active.map((call) => (
          <article key={call.id} className="call-card" data-status={call.status}>
            <div className="call-header">
              <span className="contact-avatar" aria-hidden="true">
                {callerInitials(call, chats)}
              </span>
              <div className="call-info">
                <strong className="caller-name">{callerDisplayName(call, chats)}</strong>
                <span className="muted call-meta">
                  {callDirectionLabel(call.direction)} · {channelLabel(call.isVideo)} · {routeLabels[call.route]}
                </span>
                <span className={`call-status ${callStatusClass(call.status)}`}>
                  {statusLabels[call.status]}
                </span>
              </div>
              <span
                className={`badge ${priorityClassName(call.priority)}`}
                aria-label={`Prioriteit ${call.priority}`}
              >
                {priorityLabel(call.priority) ?? `P${call.priority}`}
              </span>
            </div>

            <div className="call-timeline">
              <span>{formatCallDate(call.startedAt)}</span>
              {call.answeredAt && (
                <span>Beantwoord: {formatCallDate(call.answeredAt)}</span>
              )}
              {call.endedAt && (
                <span>Beëindigd: {formatCallDate(call.endedAt)} · {formatDuration(call.startedAt, call.endedAt)}</span>
              )}
            </div>

            <div className="call-actions">
              {actionsForStatus(call.status).map((action) => {
                switch (action) {
                  case 'accept':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button primary small"
                        onClick={() => handleAccept(call.id)}
                      >
                        Accepteren
                      </button>
                    );
                  case 'reject':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button secondary small"
                        onClick={() => handleReject(call.id)}
                      >
                        Weigeren
                      </button>
                    );
                  case 'join':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button primary small"
                        onClick={() => handleJoinCall(call)}
                        disabled={!call.roomUrl}
                      >
                        {call.isVideo ? '📹 Deelnemen' : '📞 Deelnemen'}
                      </button>
                    );
                  case 'start_avatar':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button secondary small"
                        onClick={() => handleStartAvatar(call.id)}
                      >
                        🤖 AI Avatar
                      </button>
                    );
                  case 'stop_avatar':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button secondary small"
                        onClick={() => handleStopAvatar(call.id)}
                      >
                        ⏹ Stop AI
                      </button>
                    );
                  case 'takeover':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button secondary small"
                        onClick={() => handleTakeover(call.id)}
                      >
                        ✋ Overnemen
                      </button>
                    );
                  case 'hold':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button secondary small"
                        onClick={() => handleHold(call.id)}
                      >
                        ⏸ In wacht
                      </button>
                    );
                  case 'resume':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button primary small"
                        onClick={() => handleResume(call.id)}
                      >
                        ▶ Hervatten
                      </button>
                    );
                  case 'transfer':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button secondary small"
                        onClick={() => handleTransfer(call.id)}
                      >
                        ↗ Doorschakelen
                      </button>
                    );
                  case 'end':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button secondary small danger"
                        onClick={() => handleEnd(call.id)}
                      >
                        Beëindigen
                      </button>
                    );
                  case 'call_back':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button primary small"
                        onClick={() => handleCallBack(call)}
                      >
                        ↺ Terugbellen
                      </button>
                    );
                  case 'details':
                    return (
                      <button
                        key={action}
                        type="button"
                        className="button link small"
                        onClick={() => toggleDetails(call.id)}
                      >
                        {expandedCallId === call.id ? 'Minder' : 'Details'}
                      </button>
                    );
                  default:
                    return null;
                }
              })}
            </div>

            {expandedCallId === call.id && (
              <div className="call-details">
                <dl className="call-detail-list">
                  <div>
                    <dt>ID</dt>
                    <dd>{call.id}</dd>
                  </div>
                  <div>
                    <dt>Van</dt>
                    <dd>{call.from}</dd>
                  </div>
                  <div>
                    <dt>Gestart</dt>
                    <dd>{formatCallDate(call.startedAt)}</dd>
                  </div>
                  {call.answeredAt && (
                    <div>
                      <dt>Beantwoord</dt>
                      <dd>{formatCallDate(call.answeredAt)}</dd>
                    </div>
                  )}
                  {call.endedAt && (
                    <div>
                      <dt>Beëindigd</dt>
                      <dd>
                        {formatCallDate(call.endedAt)} · {formatDuration(call.startedAt, call.endedAt)}
                      </dd>
                    </div>
                  )}
                  {call.endReason && (
                    <div>
                      <dt>Eindreden</dt>
                      <dd>{endReasonLabels[call.endReason] ?? call.endReason}</dd>
                    </div>
                  )}
                  {call.transferredTo && (
                    <div>
                      <dt>Doorgeschakeld naar</dt>
                      <dd>{call.transferredTo}</dd>
                    </div>
                  )}
                  {call.summary && (
                    <div>
                      <dt>Samenvatting</dt>
                      <dd>{call.summary}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </article>
        ))}
      </div>

      {showLog && (
        <div className="call-section call-log">
          <h3 className="call-section-title">Geschiedenis</h3>
          {loadingLogError && (
            <p className="notice error" role="alert">
              {loadingLogError}
            </p>
          )}
          {callLog.length === 0 && !loadingLogError && (
            <p className="empty-state">Geen oproepgeschiedenis.</p>
          )}
          {callLog.map((call) => (
            <article key={call.id} className="call-log-item" data-status={call.status}>
              <span className="contact-avatar" aria-hidden="true">
                {callerInitials(call, chats)}
              </span>
              <div className="call-info">
                <strong className="caller-name">{callerDisplayName(call, chats)}</strong>
                <span className="muted call-meta">
                  {callDirectionLabel(call.direction)} · {channelLabel(call.isVideo)} · {statusLabels[call.status]}
                  {call.endReason ? ` · ${endReasonLabels[call.endReason] ?? call.endReason}` : ''}
                  {call.endedAt ? ` · ${formatDuration(call.startedAt, call.endedAt)}` : ''}
                </span>
                <span className="call-timeline">
                  {formatCallDate(call.startedAt)}
                </span>
              </div>
              <span className="call-log-actions">
                {actionsForStatus(call.status).includes('call_back') && (
                  <button
                    type="button"
                    className="button primary small"
                    onClick={() => handleCallBack(call)}
                  >
                    Bel terug
                  </button>
                )}
              </span>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default CallDashboard;
